/** @prettier */
import React, { useEffect, useState } from 'react';
import { Form } from 'formik';
import { useSelector } from 'react-redux';
import { clone } from 'lodash';
import { Checkbox, Select, Row, Col, Tooltip } from 'antd';
import styled from 'styled-components';
import { InputWithIcon } from 'components-shared/inputs/InputWithIcon';
import { FaInfoCircle } from 'react-icons/fa';
import HostedTableFooter from './HostedTableFooter';
import { HOSTED_TABLE_TABS } from './HostedLookupTablesList';

const dtConvFormat = {
  DATE: 'yyyy-MM-dd',
  TIMESTAMP: 'yyyy-MM-dd HH:mm:ss.SSS'
}

const HostedColumnsForm = ({
  values,
  columns,
  setFieldValue,
  cancelForm,
  errors,
  touched,
  setFieldTouched,
  setStep,
  reservedColumnNames,
  activeTab,
  setIdColumn,
  isEdit,
  initialValues
}) => {

  const [tooltipMsg, setTooltipMsg] = useState([]);

  const dataTypes = useSelector((state) => state.hostedTables.dataTypes);

  const FaInfoCircleStyle = styled(FaInfoCircle)`
    cursor: pointer;
    margin-left: 3px;
    opacity: 0.5;
    margin-top: 6px;
  `;

  const ReservedColumnContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    p{
      font-weight : 500
    }
  `;

  useEffect(() => {
    const dataTypeVal = Object.keys(dataTypes);
    const nameValues = [];
    const typeValues = [];
    const nullableValues = [];
    const tooltipMsgs = [];
    const idColumnValues = [];
    for (let i = 0; i < columns.length; i++) {
      const dataType = columns[i].type ? columns[i].type : dataTypeVal[0];
      nameValues.push(columns[i].name);
      typeValues.push(dataType);
      nullableValues.push(columns[i].nullable);
      idColumnValues.push(columns[i].idColumn);
      let msg = `Max data limit for ${dataType} is ${dataTypes[dataType]?.defaultLength}`
      if( dataType === 'DATE' || dataType === 'TIMESTAMP'){
        msg = `Required ${dataType} format is ${dtConvFormat[dataType]}`
      }
      tooltipMsgs[i] = msg;
    }

    setFieldValue('name', nameValues);
    setFieldValue('type', typeValues);
    setFieldValue('nullable', nullableValues);
    setFieldValue('idColumn', idColumnValues);
    setFieldTouched('name', new Array(columns.length).fill(false));

    setTooltipMsg(tooltipMsgs);
  }, [columns]);

  const dataTypeLength = (index, dataType) => {
    const tooltipMsgClone = clone(tooltipMsg);
    if (dataType === 'BOOLEAN') {
      tooltipMsgClone[index] = `Data Value for ${dataType} can be true or false`;
    } else {
      tooltipMsgClone[index] = `Max data limit for ${dataType} is ${dataTypes[dataType]?.defaultLength}`;
    }
    if( dataType === 'DATE' || dataType === 'TIMESTAMP'){
      tooltipMsgClone[index] = `Data Value for ${dataType} can be in ${dtConvFormat[dataType]} format`
    }
    setTooltipMsg(tooltipMsgClone);
  };

  return (
    <div>
      <ReservedColumnContainer>
        <p>Reserved Column Names </p>
        <Tooltip title={reservedColumnNames.map((colName) => (
          <p>{colName}</p>
        )) }>
          <FaInfoCircleStyle style={{ marginLeft: '7px', marginTop: '4px' }} />
        </Tooltip>
      </ReservedColumnContainer>
    <Form className="k-form scroll-content-modal">
      {values?.name?.length > 0 &&
        columns.map((data, index) => {
          return (
            <Row key={index} justify="start">
              <Col span={7} className="rc-hook-table-row-item">
                <InputWithIcon
                  required
                  title={`Column ${index + 1}`}
                  field={`name[${index}]`}
                  placeholder={`Enter Column ${index + 1}`}
                  value={values?.name[index]}
                  icon={null}
                  disabled={ activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES ? true : false}
                />
                {typeof errors[index] !== 'undefined' && <div className="form-error">{errors[index]}</div>}
              </Col>
              <Col span={7} className="rc-hook-table-row-item">
                <label className="k-form-field required-field">
                  <span className="input-label">Type</span>
                  <div className="input-icon-wrapper">
                    <div className="icon">
                      <Tooltip title={tooltipMsg[index]}>
                        <FaInfoCircleStyle />
                      </Tooltip>
                    </div>
                    <Select
                      defaultValue="string"
                      style={{ width: '100%' }}
                      value={values?.type[index]}
                      onChange={(e) => {
                        dataTypeLength(index, e);
                        setFieldValue(`type[${index}]`, e);
                      }}
                      disabled={(activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES && index === 0 ) || isEdit ? true : false}
                      options={Object.keys(dataTypes).map((val) => {
                        return {
                          value: val,
                          label: val,
                        };
                      })}
                    />
                  </div>
                </label>
              </Col>
              {
                activeTab === HOSTED_TABLE_TABS.TABLES && 
                <Col span={5} className="rc-hook-table-row-item rc-nullable">
                <label className="k-form-field">
                  <span className="input-label">&nbsp;</span>
                  <div className="input-icon-wrapper">
                    <Checkbox
                      checked={values?.nullable[index]}
                      onChange={(e) => {
                        setFieldValue(`nullable[${index}]`, e.target.checked);
                      }}
                    >
                      Nullable
                    </Checkbox>
                  </div>
                </label>
              </Col>
              }
              {
                ((activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES ) && (data?.name !== 'CONTENT_ID') ) &&
                
                <Col span={5} className="rc-hook-table-row-item rc-nullable">
                <label className="k-form-field">
                  <span className="input-label">&nbsp;</span>
                  <div className="input-icon-wrapper">
                    <Checkbox
                      checked={values?.idColumn[index]}
                      onChange={(e) => {
                        setFieldValue(`idColumn[${index}]`, e.target.checked);
                        setFieldValue(`idColumnName`, e.target.checked ? values?.name?.[index] : "");
                      }}
                      disabled={values?.idColumn?.includes(true) && !values?.idColumn?.[index] }
                    >
                      Id Column
                    </Checkbox>
                  </div>
                </label>
              </Col>
              }
              {
                activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES && values?.idColumn[index] &&
                <Col span={5} className="rc-hook-table-row-item rc-nullable">
                <label className="k-form-field required-field">
                  <span className="input-label">Id Column Type</span>
                  <div className="input-icon-wrapper">
                    <Select
                      defaultValue="string"
                      style={{ width: '100%' }}
                      value={values.idColumnType}
                      onChange={(e) => {
                        setFieldValue(`idColumnType`, e);
                      }}
                      options={
                        [{label:'MEDIA_ID',value:'MEDIA_ID'}, {label:'CONVERSATION_ID',value:'CONVERSATION_ID'}]
                    }
                    />
                  </div>
                </label>
              </Col>
              }
            </Row>
          );
        })}

      <HostedTableFooter setStep={setStep} disabled={Object.keys(errors).length > 0} cancelForm={cancelForm} initialValues={initialValues} values={values} />
    </Form>
    </div>
  );
};

export default HostedColumnsForm;
